import apiClient from './apiClient';

// ==================== INTERFACES ====================

export interface ClientProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: string;
  address: {
    street?: string;
    city?: string;
    county?: string;
    postalCode?: string;
    country?: string;
  } | null;
  gdprConsent: boolean;
  communicationPreferences: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientPatient {
  id: string;
  name: string;
  species: string;
  breed: string;
  sex: string;
  dateOfBirth: string | null;
  age: number;
  weight: number | null;
  color: string;
  microchip: string | null;
  photo_url: string | null;
  allergies: string[];
  chronicConditions: string[];
  medications?: unknown[];
  isNeutered?: boolean;
  neuteredDate?: string | null;
  isActive?: boolean;
  lifecycleStatus?: string;
  lifecycleNotes?: string | null;
  createdAt: string;
}

export interface ClientPatientVisitSummary {
  id: string;
  date: string;
  type: string;
  diagnosis: string | null;
  treatmentPlan: string | null;
  recommendations: string | null;
  clinic: string | null;
  veterinarian: string;
}

/** Dosar medical owner-safe de la GET /client/patients/:id/medical-record */
export interface ClientOwnerMedicalTimelineEntry {
  id: string;
  kind: string;
  at: string;
  title: string;
  summary?: string | null;
  ref: { type: string; id: string };
  detail?: Record<string, unknown>;
}

export interface ClientOwnerMedicalRecord {
  animal: {
    id: string;
    name: string;
    species: string;
    breed: string | null;
    weight: number | null;
  };
  visits: Array<{
    id: string;
    visit_date: string;
    visit_type: string;
    status: string;
    clinic_name?: string;
    veterinarian_name?: string;
    diagnosis: string | null;
    treatment_plan: string | null;
    recommendations: string | null;
  }>;
  prescriptions: unknown[];
  vaccinations: unknown[];
  lab_orders: unknown[];
  documents: unknown[];
  prevention: unknown[];
  alerts: unknown[];
  visit_addenda: unknown[];
  weight_series: unknown[];
  timeline: ClientOwnerMedicalTimelineEntry[];
}

export interface ClientPatientReminder {
  id: string;
  dueAt: string;
  status: string;
  resolvedAt: string | null;
  name: string;
  kind: string;
  intervalDays: number | null;
  notes: string | null;
}

export interface ClientPatientAlert {
  id: string;
  type: string;
  title: string;
  body: string;
  createdAt: string;
  status: string;
}

export interface ClientAppointment {
  id: string;
  patient: {
    id: string;
    name: string;
    species: string;
    breed?: string;
  };
  clinic: {
    id: string;
    name: string;
    address?: string;
    phone?: string;
  };
  doctor: {
    id: string;
    name: string;
  };
  start_ts: string;
  end_ts: string;
  reason: string;
  notes: string | null;
  status: string;
  emergency: boolean;
}

export interface DashboardStats {
  totalAnimals: number;
  upcomingAppointments: number;
  totalVisits: number;
  totalSpent: number;
}

export interface AvailabilitySlot {
  start: string;
  end: string;
  available: boolean;
}

export interface CreateAppointmentRequest {
  patient_id: string;
  clinic_id: string;
  service_id?: string;
  doctor_id: string;
  start_ts: string;
  reason?: string;
}

export interface MedicalRecord {
  id: string;
  animalName: string;
  date: string;
  veterinarian: string;
  clinic: string;
  diagnosis: string;
  treatment: string;
  medications: Array<{
    name: string;
    dosage: string;
    duration: string;
  }>;
  procedures: Array<{
    name: string;
    description: string;
    date: string;
  }>;
  notes: string;
  followUpDate?: string;
}

// ==================== API CLIENT ====================
// Folosim apiClient cu path-ul /client

// ==================== PROFILE & CONSENTS ====================

export const clientDashboardService = {
  // Profile
  async getProfile(): Promise<ClientProfile> {
    const response = await apiClient.get('/client/me');
    return response.data.data;
  },

  async updateProfile(data: Partial<ClientProfile>): Promise<ClientProfile> {
    const response = await apiClient.patch('/client/me', data);
    return response.data.data;
  },

  // Consents
  async getConsents(): Promise<{ gdprConsent: boolean; communicationPreferences: any }> {
    const response = await apiClient.get('/client/consents');
    return response.data.data;
  },

  async updateConsents(data: { gdprConsent?: boolean; communicationPreferences?: any }): Promise<void> {
    await apiClient.put('/client/consents', data);
  },

  // ==================== PATIENTS (ANIMALE) ====================

  async getPatients(params?: { q?: string; lifecycle?: string; species?: string }): Promise<ClientPatient[]> {
    const response = await apiClient.get('/client/patients', { params });
    return response.data.data;
  },

  async getPatient(id: string): Promise<ClientPatient> {
    const response = await apiClient.get(`/client/patients/${id}`);
    return response.data.data;
  },

  async createPatient(data: {
    name: string;
    species: string;
    sex: string;
    breed?: string;
    color?: string;
    weight?: number | null;
    dateOfBirth?: string | null;
    microchip?: string | null;
  }): Promise<ClientPatient> {
    const response = await apiClient.post('/client/patients', data);
    return response.data.data;
  },

  async updatePatient(id: string, data: Partial<ClientPatient> & Record<string, unknown>): Promise<ClientPatient> {
    const response = await apiClient.patch(`/client/patients/${id}`, data);
    return response.data.data;
  },

  async deletePatient(id: string): Promise<{ archived?: boolean; deleted?: boolean; message?: string }> {
    const response = await apiClient.delete(`/client/patients/${id}`);
    return response.data.data;
  },

  async uploadPatientPhoto(id: string, file: File): Promise<{ photo_url: string; thumbnailUrl?: string }> {
    const fd = new FormData();
    fd.append('photo', file);
    const response = await apiClient.post(`/client/patients/${id}/photo`, fd);
    return response.data.data;
  },

  async getPatientAppointments(id: string): Promise<any[]> {
    const response = await apiClient.get(`/client/patients/${id}/appointments`);
    return response.data.data;
  },

  async getPatientMedicalRecord(id: string): Promise<ClientOwnerMedicalRecord> {
    const response = await apiClient.get(`/client/patients/${id}/medical-record`);
    return response.data.data;
  },

  async getPatientReminders(id: string): Promise<ClientPatientReminder[]> {
    const response = await apiClient.get(`/client/patients/${id}/reminders`);
    return response.data.data;
  },

  async getPatientAlerts(id: string): Promise<ClientPatientAlert[]> {
    const response = await apiClient.get(`/client/patients/${id}/alerts`);
    return response.data.data;
  },

  async getPatientVaccinations(id: string): Promise<any[]> {
    const response = await apiClient.get(`/client/patients/${id}/vaccinations`);
    return response.data.data;
  },

  async getPatientDocuments(id: string): Promise<any[]> {
    const response = await apiClient.get(`/client/patients/${id}/documents`);
    return response.data.data;
  },

  async getPatientLabResults(id: string): Promise<any[]> {
    const response = await apiClient.get(`/client/patients/${id}/lab-results`);
    return response.data.data;
  },

  async getPatientPrescriptions(id: string): Promise<any[]> {
    const response = await apiClient.get(`/client/patients/${id}/prescriptions`);
    return response.data.data || [];
  },

  async downloadPatientPrescriptionPdf(patientId: string, rxId: string): Promise<void> {
    const response = await apiClient.get(`/client/patients/${patientId}/prescriptions/${rxId}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `reteta_${rxId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // ==================== APPOINTMENTS (PROGRAMARI) ====================

  async getAppointments(params?: {
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: ClientAppointment[]; pagination: any }> {
    const response = await apiClient.get('/client/appointments', { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  async createAppointment(data: CreateAppointmentRequest): Promise<{ id: string }> {
    const response = await apiClient.post('/client/appointments', data);
    return response.data.data;
  },

  async cancelAppointment(id: string, reason?: string): Promise<void> {
    await apiClient.post(`/client/appointments/${id}/cancel`, { reason });
  },

  async requestReschedule(
    id: string,
    data: { new_start_ts: string; reason: string }
  ): Promise<void> {
    await apiClient.post(`/appointments/${id}/request-reschedule`, data);
  },

  async getAvailability(params: {
    clinic_id: string;
    service_id?: string;
    doctor_id: string;
    from: string;
    to: string;
  }): Promise<AvailabilitySlot[]> {
    const response = await apiClient.get('/client/availability', { params });
    return response.data.data;
  },

  // ==================== DASHBOARD STATS ====================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get('/client/dashboard/stats');
    return response.data.data;
  },

  // ==================== DOCUMENTS ====================

  async getDocuments(params?: {
    owner_type?: string;
    category?: string;
    patient_id?: string;
  }): Promise<any[]> {
    const response = await apiClient.get('/client/documents', { params });
    return response.data.data;
  },

  async generateUploadUrl(data: {
    filename: string;
    mime_type: string;
    file_size: number;
    owner_type?: string;
    owner_id?: string;
    category?: string;
  }): Promise<{ document_id: string; upload_url: string; storage_key: string }> {
    const response = await apiClient.post('/client/documents/upload-url', data);
    return response.data.data;
  },

  async getDownloadUrl(id: string): Promise<{ download_url: string; filename: string; mime_type: string }> {
    const response = await apiClient.get(`/client/documents/${id}/download`);
    return response.data.data;
  },

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/client/documents/${id}`);
  },

  // ==================== MESSAGES & THREADS ====================

  async getThreads(): Promise<any[]> {
    const response = await apiClient.get('/client/threads');
    return response.data.data;
  },

  async getThreadMessages(threadId: string): Promise<any[]> {
    const response = await apiClient.get(`/client/threads/${threadId}/messages`);
    return response.data.data;
  },

  async sendMessage(threadId: string, data: { body: string; attachment_storage_key?: string }): Promise<{ id: string }> {
    const response = await apiClient.post(`/client/threads/${threadId}/messages`, data);
    return response.data.data;
  },

  async markThreadAsRead(threadId: string): Promise<void> {
    await apiClient.post(`/client/threads/${threadId}/read`);
  },

  // ==================== INVOICES & PAYMENTS ====================

  async getInvoices(params?: { status?: string }): Promise<any[]> {
    const response = await apiClient.get('/client/invoices', { params });
    return response.data.data;
  },

  async getInvoice(id: string): Promise<any> {
    const response = await apiClient.get(`/client/invoices/${id}`);
    return response.data.data;
  },

  /**
   * Plată online cu Stripe Elements (PaymentIntent). Nu returnează redirect fals.
   */
  async createInvoicePaymentIntent(
    invoiceId: string,
    amount_cents?: number
  ): Promise<{
    clientSecret: string;
    paymentIntentId: string;
    publishableKey?: string;
    paymentId: string;
    amountCents: number;
    currency: string;
  }> {
    const response = await apiClient.post(`/client/invoices/${invoiceId}/payment-intent`, {
      ...(amount_cents != null ? { amount_cents } : {})
    });
    return response.data.data;
  },

  async getInvoicePDF(id: string): Promise<{ pdf_url: string; filename: string }> {
    const response = await apiClient.get(`/client/invoices/${id}/pdf`);
    return response.data.data;
  },

  // ==================== MEDICAL RECORDS ====================

  /**
   * @deprecated The client medical-records page is no longer part of the official flow.
   * Use owner-safe history via appointments, visit detail and documents instead.
   */
  async getMedicalRecords(): Promise<MedicalRecord[]> {
    throw new Error('Deprecated client medical records flow. Use appointments, visit detail and documents.');
  },

  // ==================== NOTIFICATIONS ====================

  async getNotifications(unread_only?: boolean): Promise<any[]> {
    const response = await apiClient.get('/client/notifications', {
      params: { unread_only: unread_only ? 'true' : 'false' }
    });
    return response.data.data;
  },

  async markNotificationAsRead(id: string): Promise<void> {
    await apiClient.post(`/client/notifications/${id}/read`);
  },

  // ==================== NOTIFICATION PREFERENCES ====================

  async getNotificationPreferences(): Promise<any> {
    const response = await apiClient.get('/client/notification-preferences');
    return response.data.data;
  },

  async updateNotificationPreferences(preferences: any): Promise<void> {
    await apiClient.put('/client/notification-preferences', preferences);
  },

  async testNotification(type: string, data: { title: string; message: string }): Promise<any> {
    const response = await apiClient.post('/client/test-notification', { type, ...data });
    return response.data;
  },

  // ==================== DATA EXPORT (GDPR) ====================

  async startExport(params: {
    format: 'csv' | 'xlsx' | 'json';
    entities: string[];
    dateFrom?: string;
    dateTo?: string;
    timezone?: string;
  }): Promise<{
    success: boolean;
    export_id?: string;
    status?: string;
    download_url?: string;
    filename?: string;
    size?: number;
    message?: string;
  }> {
    const response = await apiClient.post('/client/exports', params);
    return response.data;
  },

  async getExportStatus(exportId: string): Promise<{
    success: boolean;
    status: 'pending' | 'processing' | 'ready' | 'failed';
    progress: number;
    filename?: string;
    download_url?: string;
    error_message?: string;
  }> {
    const response = await apiClient.get(`/client/exports/${exportId}/status`);
    return response.data;
  },

  async getExportHistory(): Promise<any[]> {
    const response = await apiClient.get('/client/exports', { params: { limit: 10 } });
    return response.data.data;
  },
};

export default clientDashboardService;

